/**
 * StripeCheckoutOverlay
 *
 * Fullscreen modal that hosts <EmbeddedSubscriptionCheckout/> so any page
 * (BioTemplatePremiumSimple, BioTemplatePremium, FullscreenExclusive...) can
 * open the integrated Stripe checkout WITHOUT redirecting to checkout.stripe.com.
 *
 * Open by setting `open` to true with a checkout config; close via the X
 * button, ESC key, or backdrop click. Body scroll is locked while open.
 *
 * Usage:
 *   const [checkout, setCheckout] = useState<CheckoutConfig | null>(null);
 *   ...
 *   <Button onClick={() => setCheckout({ amountCents: 3999, productName: "VIP", ... })}>
 *     Pagar
 *   </Button>
 *   <StripeCheckoutOverlay
 *     open={!!checkout}
 *     config={checkout}
 *     onClose={() => setCheckout(null)}
 *   />
 */

import { useEffect } from "react";
import { X } from "lucide-react";
import EmbeddedSubscriptionCheckout from "./EmbeddedSubscriptionCheckout";

export interface CheckoutConfig {
  amountCents: number;
  productName: string;
  country: string;
  buyerEmail?: string;
  returnUrl: string;
  metadata?: Record<string, string | undefined>;
  intervalCount?: number;
  discountPercentage?: number;
  originalAmountCents?: number;
  /** Admin-generated invite code — validated server-side at checkout creation time. */
  inviteCode?: string;
  /**
   * Stripe Price ID (`price_…`) from admin VIP (country-templates / catalog).
   * Prefer this over amount-only checkout so charge matches DualCTA.
   */
  priceId?: string;
  /** Alias for priceId — same as DualCTA `stripePriceId`. */
  stripePriceId?: string;
  /** Stripe Promotion Code ID (`promo_…`) first-month discount from admin. */
  promotionCodeId?: string;
  /** true = apply canonical FIRSTANA when no promotionCodeId. */
  useDefaultPromotion?: boolean;
}

interface Props {
  open: boolean;
  config: CheckoutConfig | null;
  onClose: () => void;
  /** Header label above the embedded form. Defaults to product name. */
  title?: string;
}

export default function StripeCheckoutOverlay({ open, config, onClose, title }: Props) {
  // ESC closes the overlay
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  if (!open || !config) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-3 py-6 sm:px-6"
      onClick={(e) => {
        // Close on backdrop click only — children clicks shouldn't propagate
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title ?? config.productName}
    >
      <div className="relative w-full max-w-2xl max-h-[92dvh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute right-3 top-3 z-10 rounded-full bg-zinc-900/10 p-2 text-zinc-700 hover:bg-zinc-900/20 hover:text-zinc-900 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header — minimal so Stripe's own form takes the visual lead */}
        <div className="px-5 pt-5 pb-2 sm:px-7 sm:pt-6">
          <h2 className="text-base font-semibold text-zinc-900 sm:text-lg">
            {title ?? config.productName}
          </h2>
        </div>

        {/* Embedded checkout */}
        <div className="px-3 pb-5 sm:px-5 sm:pb-7">
          <EmbeddedSubscriptionCheckout
            amountCents={config.amountCents}
            productName={config.productName}
            country={config.country}
            buyerEmail={config.buyerEmail}
            returnUrl={config.returnUrl}
            metadata={config.metadata}
            intervalCount={config.intervalCount}
            discountPercentage={config.discountPercentage}
            originalAmountCents={config.originalAmountCents}
            inviteCode={config.inviteCode}
            priceId={config.priceId}
            stripePriceId={config.stripePriceId || config.priceId}
            promotionCodeId={config.promotionCodeId}
            useDefaultPromotion={config.useDefaultPromotion}
            onComplete={onClose}
          />
        </div>
      </div>
    </div>
  );
}
